import { useState } from 'react'  
import CustomModal from 'components/modals/CustomModal'
import { useTranslation } from 'react-i18next'
import { BUTTONS, FORM, INFORMATION, MODALS, PLACEHOLDERS, TABLE } from 'locales/locales'
import { requiredValidation } from 'utils/helpers/validations'
import axiosApi from 'app/axiosApi'
import CustomFormInput from 'components/input/custom-form-input/CustomFormInput'
import { AvatarSize } from 'components/avatar/Avatar'
import SelectImage from 'components/input/select-input/SelectImage'
import styled from 'styled-components'
import CustomText from 'components/text/CustomText'
import colors from 'utils/colors'
import Tooltip from 'components/kendo/tooltips/Tooltip'
import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs'

interface ICreateChannelModalProps {
  onClose: () => void
  fetchSystemSettings: () => Promise<void>
}

const CreateInAppBrowserModal = ({ onClose, fetchSystemSettings }: ICreateChannelModalProps) => {
  const { t } = useTranslation()
  const [generalInfo, setGeneralInfo] = useState({ avatar: '', name: '', url: '', isActive: false, isExtraTab: false })
  const [isLoading, setIsLoading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<FileList>();

  const onCreateInAppBrowser = async () => {
    const { isValid: isValidName } = requiredValidation(generalInfo.name)
    const { isValid: isValidUrl } = requiredValidation(generalInfo.url)
    if (!isValidName || !isValidUrl)
      return
    setIsLoading(true)
    const params = {
      name: generalInfo?.name.trim(),
      url: generalInfo?.url.trim(),
      isActive: generalInfo?.isActive || false,
      isExtraTab: generalInfo?.isExtraTab || false,
    }
    const url = `files/upload?from=inAppBrowser`
    try {
      const files = selectedFile && selectedFile.length > 0 ? Array.from(selectedFile) : [];
      const result = await axiosApi.uploadFile(url, files, 'file', { params: params }, undefined)
      if (result.data.success) {
        onClose()
        await fetchSystemSettings()
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onChangeGeneralInfo = (key: string, value: string | boolean) => {
    setGeneralInfo({ ...generalInfo, [key]: value })
  }

  const onChangeInAppBrowserImage = async (fileList: FileList) => {
    const fileUrl = URL.createObjectURL(fileList[0]);
    setSelectedFile(fileList);
    setGeneralInfo({ ...generalInfo, avatar: fileUrl })
  }

  return (
    <CustomModal
      title={t(MODALS.CREATE_IN_APP_BROWSER_TITLE)}
      onOk={onCreateInAppBrowser}
      closeModal={onClose}
      onCancel={onClose}
      loading={isLoading}
    >
      <Wrapper>
        <SelectImage
          type={'inAppBrowser'}
          avatar={generalInfo.avatar}
          text={generalInfo.name || 'G'}
          buttonText={BUTTONS.UPLOAD}
          avatarSize={AvatarSize.biggest}
          loading={isLoading}
          onSelectPhoto={onChangeInAppBrowserImage}
        />
        <FormWrapper>
          <CustomFormInput
            value={generalInfo.name}
            title={t(FORM.NAME) as string}
            placeholder={t(PLACEHOLDERS.IN_APP_BORWSER_TITLE)}
            onChange={(value) => onChangeGeneralInfo('name', value)}
          />
          <CustomFormInput
            value={generalInfo.url}
            title="URL"
            placeholder="URL"
            onChange={(value) => onChangeGeneralInfo('url', value)}
          />
          <StyledCheckoboxContainer>
            <CustomText typography="body3" color={colors.gray80}>
              {t('STATUS')}
            </CustomText>
            <Checkbox
              checked={generalInfo.isActive}
              style={{border: `1px solid ${colors.vime}`}}
              onChange={(event: CheckboxChangeEvent) =>
                onChangeGeneralInfo('isActive', event.value)
              }
            />
            <CustomText typography="body3" color={colors.gray80}>
              {t(TABLE.SHOW_IN_MENU)}
            </CustomText>
            <Tooltip
              content={
                <Checkbox
                  checked={generalInfo.isExtraTab}
                  style={{border: `1px solid ${colors.vime}`}}
                  onChange={(event: CheckboxChangeEvent) =>
                    onChangeGeneralInfo('isExtraTab', event.value)
                  }
                  title={t(INFORMATION.IN_APP_BROWSER_SETTINGS_EXTRA_TAB_INFO)}
                />
              }
              style={{
                maxWidth: '200px',
              }}
              position="bottom"
              parentTitle={true}
            />
          </StyledCheckoboxContainer>
        </FormWrapper>
      </Wrapper>
    </CustomModal>
  )
}

export default CreateInAppBrowserModal


const Wrapper = styled.div`
  display: flex;
  width: 100%;
  column-gap: 20px;
`

const FormWrapper = styled.div`
  width: 100%;
`
const StyledCheckoboxContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 15px;
`
